import { PayloadAction } from '@reduxjs/toolkit';

import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, Observer } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';

import { PeopleAlsoTradeParams, ReportingCall } from '../../models/reporting/types';
import GlobalService from '../../services/Global.service';
import CallsCache from '../../store-util/calls-cache/CallsCache';
import { isCallStatusPendingQueued } from '../../util/error-handling/StatusByCallHelpers';
import { getDateTime } from '../../util/temporalDeadZoneFunctions';
import { OrderTicketComponent } from '../../util/types';
import {
  getAccountsAvailableCash,
  getAccountsAvailableCashFailed,
  getAccountsAvailableCashSuccess,
} from '../common-actions';
import { processErrorInResponse, processResponse } from '../helpers';
import { INITIAL_PAYMENT_STATE } from '../payment';
import { getCurrentAccount } from '../crm/helpers';
import { RootState } from '..';

import {
  ORDERS_REQUEST_PAGE_NUMBER,
  ORDERS_REQUEST_ROWS_PER_PAGE,
  PENDING_ORDERS_REQUEST_FROM_DATE,
} from './constants';
import {
  getAccountPendingOrdersSuccess,
  getAccountTradeHistory,
  getAccountTradeHistoryFailed,
  getAccountTradeHistoryFirstAndLastTrade,
  getAccountTradeHistoryFirstAndLastTradeFailed,
  getAccountTradeHistoryFirstAndLastTradeSuccess,
  getAccountTradeHistorySuccess,
  getOpenPositions,
  getOpenPositionsFailed,
  getOpenPositionsSuccess,
  getOrderDetail,
  getOrderDetailFailed,
  getOrderDetailSuccess,
  getPeopleAlsoTradeData,
  getPeopleAlsoTradeDataCompleted,
  getPeopleAlsoTradeDataFailed,
} from './index';
import {
  AccountTradeHistoryCustomData,
  AccountTradeHistoryPayloadData,
  GetAccountsAvailableCashRequest,
  GetAccountsAvailableCashResponse,
  GetAccountTradeHistoryFirstAndLastTradePayload,
  GetAllAvailableCashPayload,
  GetOpenPositionsDataResponse,
  OpenPositionsPayloadData,
} from './types';

const getAccountsAvailableCashEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getAccountsAvailableCash.type),
  mergeMap((action: PayloadAction<GetAllAvailableCashPayload>) => new Observable((observer: Observer<any>) => {
    const callName = 'reporting/getAccountsAvailableCash';
    const currentAccount = getCurrentAccount(state$.value);
    if (!currentAccount) {
      console.error('[reporting/epics] getAccountsAvailableCashEpic - current account not found');
      return;
    }
    const ids = action.payload.accountReferenceIds ? action.payload.accountReferenceIds : [ currentAccount ];
    const requestData: GetAccountsAvailableCashRequest = {
      accountsList: ids,
      messageType: 'GetBalance',
    };
    const payloadFor404: GetAccountsAvailableCashResponse = INITIAL_PAYMENT_STATE.availableCashInfo;

    GlobalService
      .reportingService
      .getAccountsAvailableCash(requestData)
      .then(res => {
        processResponse(
          callName,
          requestData,
          res,
          observer,
          getAccountsAvailableCashSuccess,
          getAccountsAvailableCashFailed,
          false,
          null,
          null,
          null,
          null,
          { 404: payloadFor404 },
        );
      })
      .catch(error => {
        processErrorInResponse(
          callName,
          error,
          action,
          observer,
          getAccountsAvailableCashFailed,
        );
      });
  })),
);

const getOrderDetailEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getOrderDetail.type),
  mergeMap((action: PayloadAction<string>) => new Observable((observer: Observer<any>) => {
    const callName = 'reporting/getOrderDetail';
    const parentOrderId = action.payload;
    const accountReferenceId = getCurrentAccount(state$.value);
    if (!parentOrderId) {
      observer.next(getOrderDetailFailed({ status: null }));
      console.error(`[reporting/epics] getOrderDetailEpic - parentOrderId not found in action.payload for ${callName}`);
      return;
    }
    if (!accountReferenceId) {
      observer.next(getOrderDetailFailed({ status: null }));
      console.error(`[reporting/epics] getOrderDetailEpic - current account (accountReferenceId) not found in state for ${callName}`);
      return;
    }

    GlobalService
      .reportingService
      .getOrderDetailData({ accountReferenceId, parentOrderId })
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getOrderDetailSuccess,
          getOrderDetailFailed,
        );
      }).catch(e => {
        observer.next(getOrderDetailFailed(e));
      });
  })),
);

const getOpenPositionsEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getOpenPositions.type),
  mergeMap((action: PayloadAction<OpenPositionsPayloadData>) => new Observable((observer: Observer<any>) => {
    const callName = 'reporting/getOpenPositions';
    const {
      symbol,
      positionType,
      accountReferenceId = getCurrentAccount(state$.value),
      group,
    } = action.payload ?? {};
    if (!accountReferenceId) {
      observer.next(getOpenPositionsSuccess({ accounts: [] } as GetOpenPositionsDataResponse));
      console.error(`[reporting/epics] getOpenPositionsEpic - current account (accountReferenceId) not found in action.payload or state for ${callName}`);
      return;
    }
    const requestBody = {
      symbol,
      positionType,
      accountsList: [ accountReferenceId ],
      messageType: 'GetPositions',
    };

    GlobalService
      .reportingService
      .getOpenPositionsData(requestBody)
      .then(response => {
        const responseData = response?.data as GetOpenPositionsDataResponse;
        processResponse(
          callName,
          {},
          response,
          observer,
          getOpenPositionsSuccess,
          getOpenPositionsFailed,
          false,
          { group, symbol },
          responseData,
          null,
          null,
          { 404: [] },
        );
      }).catch(e => observer.next(getOpenPositionsFailed(e)));
  })),
);

export const tradeHistoryQueue: PayloadAction<any>[] = [];
const getAccountTradeHistoryEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getAccountTradeHistory.type),
  filter(action => {
    if (isCallStatusPendingQueued(ReportingCall.getAccountTradeHistory, state$.value.reporting.statusByCall)) {
      tradeHistoryQueue.push(action);
      return false;
    }
    return true;
  }),
  mergeMap((action: PayloadAction<AccountTradeHistoryPayloadData>) => new Observable((observer: Observer<any>) => {
    const callName = 'reporting/getAccountTradeHistory';
    const accountReferenceId = getCurrentAccount(state$.value);
    if (!accountReferenceId) {
      observer.next(getAccountTradeHistorySuccess({ accounts: [] }));
      console.error(`[reporting/epics] getAccountTradeHistoryEpic - current account (accountReferenceId) not found in state for ${callName}`);
      return;
    }

    const {
      fromDate = PENDING_ORDERS_REQUEST_FROM_DATE,
      toDate = getDateTime(),
      pageNumber = ORDERS_REQUEST_PAGE_NUMBER,
      rowsPerPage = ORDERS_REQUEST_ROWS_PER_PAGE,
      symbols_StringList,
      statuses_StringList,
      tenNStatuses_StringList,
      isPendingOrdersCall,
      isSymbolDetails,
    } = action.payload;

    const requestBody: AccountTradeHistoryPayloadData = {
      fromDate,
      toDate,
      pageNumber,
      symbols_StringList,
      statuses_StringList,
      rowsPerPage,
      tenNStatuses_StringList,
      accountsList: [ accountReferenceId ],
      messageType: 'GetOrders',
    };

    GlobalService
      .reportingService
      .getAccountTradeHistory(requestBody)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          isPendingOrdersCall ? getAccountPendingOrdersSuccess : getAccountTradeHistorySuccess,
          getAccountTradeHistoryFailed,
          false,
          { isSymbolDetails,
            queueLength: tradeHistoryQueue.length,
            isPendingOrdersCall,
          } as AccountTradeHistoryCustomData,
          null,
          null,
          null,
          { 404: [] },
        );
        if (tradeHistoryQueue.length) {
          let theAction = tradeHistoryQueue.shift() as PayloadAction<any>;
          if (!theAction.payload.customData) theAction.payload.customData = {};
          theAction.payload.customData.isFromQueue = true;
          theAction.payload.customData.queueLength = tradeHistoryQueue.length;
          observer.next(theAction);
        }
      }).catch(e => observer.next(getAccountTradeHistoryFailed(e)));
  })),
);

const getPeopleAlsoTradeDataEpic = (action$: Observable<any>, state$: StateObservable<RootState>) => action$.pipe(
  ofType(getPeopleAlsoTradeData.type),
  filter(() => state$.value.app.isServicesReady),
  filter(() => {
    const component: OrderTicketComponent = 'people-also-trade';
    const { toBeUpdated } = CallsCache.checkRequest('customStockList', component);
    const isEpicEnabled = toBeUpdated ?? false;
    // @@@ debug
    console.log(`[@@@] update people - ${isEpicEnabled}`); // eslint-disable-line
    return isEpicEnabled;
  }),
  mergeMap(action => new Observable((observer: Observer<any>) => {
    const callName = 'reporting/getPeopleAlsoTradeData';
    const accountReferenceId = getCurrentAccount(state$.value);

    const params: PeopleAlsoTradeParams = {
      accountReferenceId,
      daysBack: 2,
      tenNAccounstOnly: false,
    };

    GlobalService
      .reportingService
      .getPeopleAlsoTradeData(params)
      .then(response => {
        processResponse(
          callName,
          {},
          response,
          observer,
          getPeopleAlsoTradeDataCompleted,
          getPeopleAlsoTradeDataFailed,
        );
      })
      .catch(error => processErrorInResponse(
        callName,
        error,
        action,
        observer,
        getPeopleAlsoTradeDataFailed,
      ));
  })),
);

const getAccountTradeHistoryFirstAndLastTradeEpic = (
  action$: Observable<any>,
  state$: StateObservable<RootState>,
) => action$.pipe(
  ofType(getAccountTradeHistoryFirstAndLastTrade.type),
  mergeMap((action: PayloadAction<GetAccountTradeHistoryFirstAndLastTradePayload>) => new Observable(
    (observer: Observer<any>) => {
      const callName = 'reporting/getAccountTradeHistoryFirstAndLastTrade';

      const requestBody: GetAccountTradeHistoryFirstAndLastTradePayload = {
        accountsList: action.payload.accountsList,
      };

      GlobalService
        .reportingService
        .getAccountTradeHistoryFirstAndLastTrade(requestBody)
        .then(response => {
          processResponse(
            callName,
            {},
            response,
            observer,
            getAccountTradeHistoryFirstAndLastTradeSuccess,
            getAccountTradeHistoryFirstAndLastTradeFailed,
            false,
            null,
            null,
            null,
            null,
            { 404: [] },
          );
        }).catch(e => observer.next(getAccountTradeHistoryFirstAndLastTradeFailed(e)));
    },
  )),
);

export default combineEpics(
  getAccountsAvailableCashEpic,
  getOrderDetailEpic,
  getOpenPositionsEpic,
  getAccountTradeHistoryEpic,
  getPeopleAlsoTradeDataEpic,
  getAccountTradeHistoryFirstAndLastTradeEpic,
);
